import DisableDevtool from 'disable-devtool';
import { jwtDecode } from 'jwt-decode';
import React, { lazy, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Routes, useLocation, useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/store';

import AutoCredit from './components/App/AutoCred/auto-credit';
import ClientInfo from './components/App/Clients/client-info/client-info';
import Clients from './components/App/Clients/clients-list/clients';
import Leasing, { IeOrder, LlpOrder } from './components/App/Leasing';
import ExistedOrder from './components/App/NewOrder/ExistedOrder';
import NewOrder from './components/App/NewOrder/NewOrder';
import { AuthModal } from './components/Auth/login';
import ClientRoutes from './components/Client/client-routes';
import FaqRoutes from './components/Faq/FaqRoutes';
import { Layout } from './components/Layout/Layout';
import CheckAuth from './components/Shared/routes/check-auth';
import LanguageGuard from './components/Shared/routes/language-guard';
import { ProtectedRoute } from './components/Shared/routes/private-route';
import {
  ADMIN_ROUTES,
  CLIENT_ROUTES,
  FAQ_ROUTES,
  ROUTES
} from './components/Shared/routes/routes';
import SuspenseRoute from './components/Shared/routes/suspense-route';
import useAppNavigate from './hooks/useAppNavigation';
import { abortControllerManager } from './services/helpers/api';
import { checkCacheClearedAndNotify } from './services/helpers/cacheData';
import { setNavigate } from './services/helpers/navigate-helper';
import { getUserToken } from './services/user-hash';
import { updateUserAccesses } from './store/userInfo.slice';

const ServiceDeskCreate = lazy(
  () => import('./components/Shared/widgets/service-desk-create')
);
const ServiceDeskRequest = lazy(
  () => import('./components/Shared/widgets/service-desk-request')
);
const ServiceDeskTable = lazy(
  () => import('./components/Shared/widgets/service-desk-table')
);

const SecurityList = lazy(
  () => import('./components/App/Security/SecurityList/security-list')
);
const SecurityRequest = lazy(
  () => import('./components/App/Security/SecurityRequest/security-request')
);

const LoginPage = lazy(() => import('./components/Auth/login/login'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const NotFound = lazy(() => import('./components/Shared/widgets/errors/404'));

const AdminRoutes = lazy(() => import('./components/AdminPanel/admin-routes'));
const MainPage = lazy(() => import('./components/App/main-page/main-page'));
const BankSummary = lazy(
  () => import('./components/App/main-page/BankSummary/bank-summary')
);
const Personal = lazy(() => import('./components/App/Personal/Personal'));

const Insurance = lazy(() => import('./components/App/Insurance/Insurance'));
const CreateInsurance = lazy(
  () => import('./components/App/Insurance/insurance-order/create-insurance')
);
const InsuranceOrder = lazy(
  () => import('./components/App/Insurance/insurance-order/insurance-order')
);

const Reports = lazy(() => import('./components/App/Reports'));
const Approvement = lazy(
  () => import('./components/App/Approvement/approvement')
);

const PosCredit = lazy(
  () => import('./components/App/PosCred/PosCreds/pos-creds')
);
const PosCreditApplication = lazy(
  () =>
    import('./components/App/PosCred/PosCredApclication/pos-cred-application')
);
const Leads = lazy(() => import('./components/App/Leads/page'));
const LeadsUpdate = lazy(() => import('./components/App/Leads/Update/page'));

const BankShareManagement = lazy(
  () => import('./components/App/BankShareManagement/BankShareManagement')
);

const ACCEPTED_ROUTES = {
  main: <Route key={'main'} path={ROUTES.home} element={<MainPage />} />,
  'auto-credit': (
    <React.Fragment key={'auto-credit'}>
      <Route path={ROUTES.autoCredit} element={<AutoCredit />} />
      <Route path={ROUTES.autoCreditCreate} element={<NewOrder />} />
      <Route path={ROUTES.autoCreditRequest()} element={<ExistedOrder />} />
    </React.Fragment>
  ),
  insurances: (
    <React.Fragment key={'insurances'}>
      <Route path={ROUTES.insurances} element={<Insurance />} />
      <Route path={ROUTES.insurancesCreate} element={<CreateInsurance />} />
      <Route path={ROUTES.insurancesRequest()} element={<InsuranceOrder />} />
    </React.Fragment>
  ),
  leasing: (
    <React.Fragment key={'leasing'}>
      <Route path={ROUTES.leasings} element={<Leasing />} />
      <Route path={ROUTES.leasingIeRequest()} element={<IeOrder />} />
      <Route path={ROUTES.leasingLlpRequest()} element={<LlpOrder />} />
    </React.Fragment>
  ),
  'pos-credit': (
    <React.Fragment key={'pos-credit'}>
      <Route path={ROUTES.posCredit} element={<PosCredit />} />
      <Route
        path={ROUTES.posCreditRequest()}
        element={<PosCreditApplication />}
      />
      <Route
        path={ROUTES.posCreditCreate()}
        element={<PosCreditApplication />}
      />
    </React.Fragment>
  ),
  clients: (
    <React.Fragment key={'clients'}>
      <Route path={ROUTES.clients} element={<Clients />} />
      <Route path={ROUTES.client()} element={<ClientInfo />} />
    </React.Fragment>
  ),
  reports: (
    <Route key={'reports'} path={ROUTES.reports} element={<Reports />} />
  ),
  leads: (
    <React.Fragment key={'leads'}>
      <Route path={ROUTES.leads} element={<Leads />} />
      <Route path={ROUTES.leadsUpdate()} element={<LeadsUpdate />} />
    </React.Fragment>
  ),
  'share-management': (
    <Route
      key={'bank-share-management'}
      path={ROUTES.shareManagement}
      element={<BankShareManagement />}
    />
  )
};

const isProduction = process.env.NODE_ENV === 'production';

export const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { navigate: appNavigate } = useAppNavigate();

  const dispatch = useAppDispatch();
  const { headerConfig } = useAppSelector((state) => state.app);
  const { isUserAuth } = useAppSelector((state) => state.auth);
  const {
    dealer,
    menu,
    userInfo,
    userAccesses: {
      security: { hasGeneralAccessToSecurity },
      global: { isAdmin }
    }
  } = useAppSelector((state) => state.userInfo);

  // Set global navigate to access from everywhere
  setNavigate(navigate, appNavigate);

  useEffect(() => {
    checkCacheClearedAndNotify();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'dealer') {
        const localStorageDealer = localStorage.getItem('dealer');
        if (
          dealer &&
          localStorageDealer &&
          Number(dealer.code) === Number(localStorageDealer)
        )
          return;

        try {
          window.location.href =
            menu[0].code === 'main' ? '/' : `/${menu[0].code}`;
        } catch {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [menu]);

  useEffect(() => {
    const accessToken = getUserToken().accessToken;
    if (accessToken) {
      const user: { actions: number[]; username: string } =
        jwtDecode(accessToken);
      const isAdmin = !!user.actions.includes(0);

      // 159 - Менеджер
      const hasAccessToSpeechAnalytics =
        !!user.actions.includes(159) || isAdmin;

      dispatch(
        updateUserAccesses({
          security: {
            hasGeneralAccessToSecurity: isAdmin || !!user.actions.includes(500)
          },
          global: {
            isAdmin
          },
          speechAnalytics: {
            hasAccessToSpeechAnalytics
          }
        })
      );
      if (isProduction && !isAdmin) {
        DisableDevtool({
          disableMenu: false,
          ignore: ['/login', /\/reset_password\/.+/]
        });
      }
    }
  }, [isUserAuth, userInfo]);

  useEffect(() => {
    abortControllerManager.abortAll();

    const handleLoad = () => {
      const existedManifest = document.querySelector('link[rel="manifest"]');
      // Отключение скачивания приложения по заданным маршрутам
      if ([/\/\w+\/client\//].some((regex) => regex.test(location.pathname))) {
        existedManifest?.remove();
      } else if (!existedManifest) {
        const manifest = document.createElement('link');
        manifest.rel = 'manifest';
        manifest.href = '/manifest.json';
        document.head.appendChild(manifest);
      }
    };
    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (menu.length === 0) return;

    const sortedMenu = structuredClone(menu).sort(
      (a, b) => a.position - b.position
    );

    const isFirstMain = sortedMenu[0]?.code === 'main';
    if (isFirstMain) sortedMenu[0].code = '';

    // Устанавливаем route по умолчанию для пользователя
    const route = isFirstMain
      ? sortedMenu[1]?.code || sortedMenu[0]?.code
      : sortedMenu[0]?.code;

    const defaultRoute = `/${route || ''}`;

    if (
      (location.pathname === '/' &&
        !menu.find((item) => item.code === 'main')) ||
      (!isAdmin && location.pathname.startsWith(ADMIN_ROUTES.root))
    ) {
      navigate(defaultRoute, { replace: true });
    }
  }, [location.pathname, isAdmin, menu]);

  return (
    <>
      {!!headerConfig.helmetTitle && (
        <Helmet title={headerConfig.helmetTitle} />
      )}
      <Routes>
        <Route element={<CheckAuth AuthModal={<AuthModal />} />}>
          <Route path="/" element={<Layout />}>
            {!menu.find((item) => item.code === 'main') && (
              <Route path="/" element={<NotFound />} />
            )}

            <Route
              element={
                <ProtectedRoute
                  redirectPath={ROUTES.login}
                  isAllowed={isUserAuth}
                />
              }
            >
              <Route element={<SuspenseRoute />}>
                {menu.map((item) => {
                  const route =
                    ACCEPTED_ROUTES[item.code as keyof typeof ACCEPTED_ROUTES];
                  return route ?? null;
                })}

                <Route
                  path={ROUTES.bankSummaryTotal}
                  element={<BankSummary />}
                />
                <Route path={ROUTES.bankSummary()} element={<BankSummary />} />
                <Route path={ROUTES.personal} element={<Personal />} />

                {isAdmin && (
                  <Route
                    path={ROUTES.serviceDesk}
                    element={<ServiceDeskTable />}
                  />
                )}
                <Route
                  path={ROUTES.serviceDeskCreate}
                  element={!isAdmin ? <ServiceDeskCreate /> : <NotFound />}
                />
                <Route
                  path={ROUTES.serviceDeskRequest()}
                  element={<ServiceDeskRequest />}
                />
                {hasGeneralAccessToSecurity && (
                  <>
                    <Route path={ROUTES.security} element={<SecurityList />} />
                    <Route
                      path={ROUTES.securityRequest()}
                      element={<SecurityRequest />}
                    />
                  </>
                )}

                {isAdmin && (
                  <Route
                    key="admin-panel"
                    path={`${ADMIN_ROUTES.root}/*`}
                    element={<AdminRoutes />}
                  />
                )}
              </Route>
            </Route>

            <Route element={<SuspenseRoute />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Route>

        <Route
          path={`/:lang${CLIENT_ROUTES.root}/*`}
          element={
            <LanguageGuard>
              <ClientRoutes />
            </LanguageGuard>
          }
        />

        <Route element={<CheckAuth AuthModal={<AuthModal />} />}>
          <Route element={<SuspenseRoute />}>
            <Route path={`${FAQ_ROUTES.root}/*`} element={<FaqRoutes />} />
          </Route>
        </Route>

        <Route element={<SuspenseRoute />}>
          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.resetPassword()} element={<ResetPassword />} />
          <Route path={ROUTES.approvements} element={<Approvement />} />
        </Route>
      </Routes>
    </>
  );
};
